import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'

import { useOrderRedux, useCartRedux } from '@hooks'
import { ROUTES, customToast } from '@common'
import { Loader } from '@components'
import { CREATE_PAYMENT_INTENT, ErrorTrackingAPI } from '@services'
import { storageHelpers } from '@utils'

export default function AdvanceTakeawayPaymentPage() {
    const { orderId } = useParams()
    const navigate = useNavigate()
    const { completeOrder, fetchOrderById } = useOrderRedux()
    const { clearCart } = useCartRedux()
    const [isCreatingPayment, setIsCreatingPayment] = useState(false)
    const hasCreatedPaymentRef = useRef(false)

    const [createPaymentIntent] = useMutation(CREATE_PAYMENT_INTENT, {
        onCompleted: data => {
            setIsCreatingPayment(false)
            if (data?.createPaymentIntent?.result) {
                const { id: paymentIntentId, client_secret: clientSecret, next_action } = data.createPaymentIntent.paymentIntent
                const qrCodeSVG = next_action?.paynow_display_qr_code?.image_url_svg
                const hostedVoucherUrl = next_action?.paynow_display_qr_code?.hosted_instructions_url

                storageHelpers.setPaymentOrigin('advance-takeaway')
                navigate(
                    ROUTES.Payment.root
                        .replace(':orderId', completeOrder.orderId)
                        .replace(':paymentIntentId', paymentIntentId)
                        .replace(':clientSecret', clientSecret),
                    { replace: true, state: { qrCodeSVG, hostedVoucherUrl } }
                )
            } else {
                customToast.error(data?.createPaymentIntent?.error || 'Failed to create payment intent')
                // Navigate back to order complete page on error
                navigate(ROUTES.Order.complete.replace(':orderId', orderId), { replace: true })
            }
        },
        onError: err => {
            setIsCreatingPayment(false)
            ErrorTrackingAPI.sendError(err, 'AdvanceTakeawayPaymentPage', 'createPaymentIntent')
            customToast.error(err?.message || 'Failed to create payment intent')
            // Navigate back to order complete page on error
            navigate(ROUTES.Order.complete.replace(':orderId', orderId), { replace: true })
        },
    })

    useEffect(() => {
        clearCart()
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (completeOrder === null) {
            const fetchOrderByIdAsync = async () => {
                const res = await fetchOrderById(orderId)
                if (!res.payload) {
                    navigate('/not-found', { replace: true })
                }
            }

            fetchOrderByIdAsync()
        }
    }, [completeOrder, orderId, fetchOrderById, navigate])

    useEffect(() => {
        // Automatically create payment intent when order is loaded
        // Use ref to ensure we only create payment intent once
        if (completeOrder && !isCreatingPayment && !hasCreatedPaymentRef.current) {
            hasCreatedPaymentRef.current = true
            setIsCreatingPayment(true)
            createPaymentIntent({
                variables: {
                    input: {
                        orderId: completeOrder.orderId,
                        orderNumber: completeOrder.number,
                        amount: completeOrder.summary?.finalAmount || 0,
                    },
                },
            })
        }
    }, [completeOrder, isCreatingPayment, createPaymentIntent])

    return <Loader />
}
